import { type MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import { ListItemText, Menu, MenuItem, type MenuListProps } from '@mui/material'

import type { CRUDOrderOptions, Order, RxCardItem } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import { getIsCreatedPrescriptionChewyRx } from '~/utils/prescription'

import { getInvoiceIdFromRxCardItem } from '../rxCardItem'
import { usePrescriptionCardActions } from '../usePrescriptionCardActions'
import { isOrderedDrugCard } from './rxCardUtilities'

import styles from './RxOrderTableMenu.module.scss'

const MENU_ID_PREFIX = 'rx-order-table-menu'
const BUTTON_ID_PREFIX = 'rx-order-table-menu-button'

interface ExtendedMenuListProps extends MenuListProps {
  'data-click-exclude'?: string
}

interface MenuActionItem {
  label: string
  onClick: () => void
}

export interface RxOrderTableMenuProps {
  index?: number
  isInvoice?: boolean
  item: RxCardItem
  onUpdateRefill?: (refill: Order, options: CRUDOrderOptions) => void
  refill?: Prescription
  showPrint?: boolean
}

const RxOrderTableMenu = ({
  item,
  refill,
  isInvoice = false,
  index = 0,
  onUpdateRefill,
  showPrint = true,
}: RxOrderTableMenuProps) => {
  const { t } = useTranslation(['Common'])
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {
    handlePrint,
    handleViewInvoice,
    handleViewRefill,
    handlePreviewPrescription,
    handlePreviewRefill,
    getActionVisibility,
    getSoapUrl,
    persistClickedItem,
  } = usePrescriptionCardActions({
    item,
    index,
    isInvoice,
    onUpdateRefill,
    refill,
    showPrint,
  })

  const visibility = getActionVisibility()

  // Ordered drugs only show View SOAP + View Invoice — no Print or Preview (mirrors old OrderedDrugCard)
  const isOrderedDrug = isOrderedDrugCard(item)

  const prescription = refill ?? item.prescription
  const prescriptionId = prescription?.id
  const menuIdentifier =
    prescriptionId ||
    prescription?.logId ||
    prescription?.inventoryId ||
    getInvoiceIdFromRxCardItem(item)

  if (!visibility.canShowMenu || !menuIdentifier) return null

  const menuId = `${MENU_ID_PREFIX}-${menuIdentifier}`
  const buttonId = `${BUTTON_ID_PREFIX}-${menuIdentifier}`

  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => setAnchorEl(null)

  const soapId = refill?.soapId ?? item.soapId
  const isChewy = getIsCreatedPrescriptionChewyRx(
    item.prescription?.prescriptionType ?? null,
  )

  const getPrescriptionToPrint = (): Prescription => {
    if (refill) return { ...refill, parent: item.prescription }
    return item.prescription
  }

  const handlePreviewClick = () => {
    if (refill) {
      if (isChewy) {
        handlePreviewPrescription()
      } else {
        handlePreviewRefill(refill)
      }
    } else {
      handlePreviewPrescription()
    }
  }

  const menuItems: MenuActionItem[] = [
    visibility.shouldShowPrint &&
      !isOrderedDrug && {
        label: t('Common:PRINT_ACTION'),
        onClick: () => handlePrint(getPrescriptionToPrint()),
      },
    visibility.shouldShowInvoice && {
      label: t('Common:VIEW_INVOICE'),
      onClick: () =>
        refill
          ? handleViewRefill(refill)
          : handleViewInvoice(getInvoiceIdFromRxCardItem(item)),
    },
    visibility.shouldShowPreview &&
      !isOrderedDrug && {
        label: t('Common:PREVIEW_ACTION'),
        onClick: handlePreviewClick,
      },
    visibility.shouldShowSoap &&
      soapId && {
        label: t('Common:VIEW_SOAP'),
        onClick: () => {
          persistClickedItem(index)
          navigate(getSoapUrl(soapId))
        },
      },
  ].filter((menuItem): menuItem is MenuActionItem => Boolean(menuItem))

  if (!menuItems.length) return null

  return (
    <>
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label={t('Common:ACTIONS')}
        className={styles.menuTrigger}
        id={buttonId}
        type="button"
        onClick={handleMenuOpen}
      >
        <MoreHoriz fontSize="small" />
      </button>
      <Menu
        MenuListProps={
          {
            'aria-labelledby': buttonId,
            'data-click-exclude': 'true',
          } as ExtendedMenuListProps
        }
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id={menuId}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={(e) => {
          if (
            e &&
            'stopPropagation' in e &&
            typeof e.stopPropagation === 'function'
          ) {
            e.stopPropagation()
          }
          handleMenuClose()
        }}
      >
        {menuItems.map(({ label, onClick }) => (
          <MenuItem
            key={label}
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation()
              onClick()
              handleMenuClose()
            }}
          >
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default RxOrderTableMenu
